/* rb-b-peek.js — long facets show 12 values and a "See all". DESKTOP ONLY.

   THE PROBLEM, MEASURED
   Brand renders 100 rows and stood 4046px tall when opened. The filter panel
   went from ~1400px to 5370px, so Brand alone was three quarters of it and
   every facet below it — Product type, Size, Price, Shop — was pushed far past
   the bottom of a panel only ~878px tall. Now: Brand 620px, panel 1945px.

   WORTH KNOWING WHILE READING THIS: 100 is not the number of brands, it is
   Shopify's cap. The catalog has 160 vendors, so 60 cannot be filtered to at
   all on /collections/all — Filson, Vuori, Goorin Bros, Klean Kanteen and
   others. This file does NOT fix that and cannot: the cap is applied
   server-side before the HTML exists, exactly like the 12 missing sizes.

   WHY NOT REUSE openFacets() IN rb-b-sidebar.js
   It already builds this button, but it is only called for the 'open' and 'mix'
   variants, and it also force-opens every group it touches. Mark's variant is
   'style', where facets stay collapsed until clicked — so calling it would have
   changed the default state of the whole panel to get one button. This does the
   peek WITHOUT touching open/closed state.

   THE THRESHOLD IS 12 ON PURPOSE: it matches FIND_THRESHOLD in rb-b-sidebar.js,
   so any list long enough to be truncated is also long enough to have been
   given a search box. Nothing becomes unreachable — there is always both a
   "See all" and a search.

   FOUR THINGS IT HAS TO COOPERATE WITH:

   1. THE SEARCH BOX. addFinder()'s own input handler sets display on every row,
      so typing correctly reveals matches beyond the first 12 — but clearing the
      box makes it show ALL rows again, undoing the peek. So this listens for
      input too (delegated on the group, which sidesteps any question of which
      module attached its listener first) and re-paints when the box empties.
      While a search is active the button hides; "See all" is meaningless then.

   2. A SELECTED VALUE BELOW THE FOLD. A checked row is always kept visible even
      when collapsed, so a shopper who picked a brand from deep in the list can
      still see and un-pick it.

   3. CLOSING THE FACET RESETS IT. Mark: "i like how brand looks when it gets
      collapsed though you should have to click show all 100 again". Without
      this, expanding to 100 and then closing the accordion left it expanded, so
      reopening dumped all 100 rows back into the panel with no obvious cause.
      Collapsing on close means the facet always reopens at its short, calm
      state. The listener is bound directly to the <details> because 'toggle'
      does not bubble.

   4. A REAL SECTION SWAP, via rbbSwap.changed('peek') — the section observer
      also fires for lazy-loaded product images (9 bursts in one ordinary
      scroll) and redoing this work on each of those is what made the page
      stutter. The debounce is 120ms so addFinder() has already run and the row
      set is settled before anything is measured. */

(function () {
  'use strict';

  var DESKTOP = '(min-width: 981px)';
  var PEEK = 12;

  function isDesktop() { return window.matchMedia(DESKTOP).matches; }

  function decorate(grp) {
    // Size is chips and dropdowns with its own subheadings, not a plain list.
    if (grp.classList.contains('collection-sidebar__filter-group--size')) return;
    if (grp.querySelector('.rbb-peek')) return;

    var list = grp.querySelector('.collection-sidebar__items');
    if (!list) return;

    var rows = Array.prototype.slice.call(grp.querySelectorAll('.collection-sidebar__item'));
    if (rows.length <= PEEK) return;

    var expanded = false;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'rbb-more rbb-peek';   // rbb-more carries the styling

    function searching() {
      var f = grp.querySelector('.rbb-find');
      return !!(f && f.value.trim());
    }

    function paint() {
      if (searching()) { btn.hidden = true; return; }   // addFinder owns the rows
      btn.hidden = false;
      rows.forEach(function (row, i) {
        var on = !!row.querySelector('input:checked');
        row.style.display = (expanded || i < PEEK || on) ? '' : 'none';
      });
      btn.textContent = expanded ? 'Show fewer' : 'See all ' + rows.length;
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    btn.addEventListener('click', function () { expanded = !expanded; paint(); });

    /* Delegated, so it does not matter whether addFinder() has inserted the
       search box yet when this runs. */
    grp.addEventListener('input', function (e) {
      if (!e.target || !e.target.classList) return;
      if (!e.target.classList.contains('rbb-find')) return;
      paint();
    });

    /* Closing the facet folds it back to 12, so it always reopens short and you
       click "See all" again deliberately. Bound on the element because 'toggle'
       does not bubble. */
    grp.addEventListener('toggle', function () {
      if (!grp.open && expanded) { expanded = false; paint(); }
    });

    list.parentNode.insertBefore(btn, list.nextSibling);
    paint();
  }

  function run() {
    if (!isDesktop()) return;
    document.querySelectorAll('.collection-sidebar__filter-group').forEach(decorate);
  }

  function boot() {
    run();

    var section = document.querySelector('.js-section__collection');
    if (!section || !window.MutationObserver) return;

    var pending = null;
    new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(function () {
        if (window.rbbSwap && !window.rbbSwap.changed('peek')) return;
        run();
      }, 120);
    }).observe(section, { childList: true, subtree: true });

    window.addEventListener('resize', function () {
      if (window.rbbSwap) window.rbbSwap.invalidate('peek');
      run();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
